// NOTE: The following imports back the OLD template sections that no longer exist
// in the current Initial Psych Evolution form. They are kept (commented) for
// discoverability now that those sections are commented out below.
// import {
//   PATIENT_ID_VERIFICATION_METHOD_MAPPER,
//   SAFETY_ASSESSMENT_SUICIDE_RISK_MAPPER,
//   SAFETY_PLAN_COMPLETED_MAPPER,
// } from "../../pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/form-components/InitialPsychotherapy/modals/PsychiatricEvolutionModal/PsychiatricEvolutionFormConstant";
// import { NonNullableDate } from "../../utils/date-time-utility";
// import { CarePlanTemplateList, KeyValPair, OrderList, RenderTextWithContainerPattern, ReportContainer } from "../constant";
// import PsychiatricEvolutionPlanMapper from "./components/PsychEvolutionPlanMapper";
// import ClinicalInstructionsPlanMapper from "./components/ClinicalInstructionsPlanMapper";
// import AdjunctTherapyRecommendationsMapper from "./components/AdjunctTherapyRecommendationsMapper";
// import FormsToBeProvidedMapper from "./components/FormsToBeProvidedMapper";
// import PatientRequestsMapper from "./components/PatientRequestsMapper";
// import WeightTherapyRadioResult from "./components/WeightTherapyRadioResult";
// import WeightTherapiesDurationRadioResult from "./components/WeightTherapiesDurationRadioResult";

import {
  CarePlanTemplateList,
  OrderList,
  RenderTextWithContainerPattern,
  ReportSection,
  TherapyNotesList,
} from "../constant";
import PsychoeducationSection from "../_shared/PsychoeducationSection";
import AllergiesBlock from "../_shared/AllergiesBlock";
import HistoryBlock from "../_shared/HistoryBlock";
import MedicationsBlock from "../_shared/MedicationsBlock";
import VitalsBlock from "../_shared/VitalsBlock";
import RosBlock from "../_shared/RosBlock";
import PhysicalExamBlock from "../_shared/PhysicalExamBlock";
import ScreeningAssessmentBlock from "../_shared/ScreeningAssessmentBlock";
import AssessmentBlock from "../_shared/AssessmentBlock";
import PrescriptionsBlock from "../_shared/PrescriptionsBlock";
import VaccinesBlock from "../_shared/VaccinesBlock";
import CptCodesBlock from "../_shared/CptCodesBlock";
// Single mapper for the 3 new modals (Psychiatric Evolution, MSE, Risk Assessment)
import InitialPsychModalsMapper from "./components/InitialPsychModalsMapper";

interface InitialPsychEvolutionTemplateProps {
  data: any;
}

const InitialPsychEvolutionTemplate = ({
  data,
}: InitialPsychEvolutionTemplateProps) => {
  return (
    <>
      {/* ── Subjective ── */}
      <ReportSection label="Subjective">
        <RenderTextWithContainerPattern
          label="Chief Complaint"
          val={data?.subjectiveChiefComplaint}
        />
        <RenderTextWithContainerPattern
          label="History of Present Illness"
          val={data?.historyOfPresentIllness}
        />
        <RenderTextWithContainerPattern
          label="Subjective"
          val={data?.subjectiveFreeNote}
        />
        <AllergiesBlock data={data} />
        <HistoryBlock data={data} />
        <RenderTextWithContainerPattern label="Habits" val={data?.habits} />
        <MedicationsBlock data={data} />

        {/* ── Psychiatric Evolution (modal) ──
          Past Psychodynamic / Medical / Substance Use / Family Psychiatric /
          Social history — checked options only + per-section note. */}
        <InitialPsychModalsMapper data={data} type="psychiatricEvolution" />
      </ReportSection>

      {/* ── Objective ── */}
      <ReportSection label="Objective">
        <RenderTextWithContainerPattern
          label="Objective"
          val={data?.objectiveFreeNote}
        />
        <VitalsBlock data={data} />
        <PhysicalExamBlock data={data} />
        <RosBlock data={data} />
        <ScreeningAssessmentBlock data={data} />

        {/* ── Mental Status Examination (MSE) (modal) ──
          Appearance, Behavior, Speech, Mood/Affect, Thought Process/Content,
          Perception, Cognition, Insight, Judgment — checked options only. */}
        <InitialPsychModalsMapper data={data} type="mse" />
      </ReportSection>

      {/* ── Assessment ── */}
      <ReportSection label="Assessment">
        <AssessmentBlock data={data} />

        {/* ── Risk Assessment (modal) ──
          Selected risk level (radio) + free-text note. */}
        <InitialPsychModalsMapper data={data} type="riskAssessment" />
      </ReportSection>

      {/* ── Plan ──
          Mirrors the Psychiatric Note template plan section. */}
      <ReportSection label="Plan">
        <RenderTextWithContainerPattern label="Plan" val={data?.planFreeNote} />
        <PrescriptionsBlock data={data} />
        <VaccinesBlock data={data} />
        <OrderList orders={data?.patientOrders} />
        <CptCodesBlock data={data} />
        <RenderTextWithContainerPattern
          label="Care Plan"
          val={data?.carePlan}
        />
        <CarePlanTemplateList carePlanTemplates={data?.carePlanTemplates} />
        <TherapyNotesList therapyNotes={data?.therapyNotes} />
        <RenderTextWithContainerPattern
          label="Instructions"
          val={data?.instruction}
        />
      </ReportSection>

      {/* ──────────────────────────────────────────────────────────────────
          OLD SECTIONS — commented out (no longer present in the current form).
          Kept for discoverability.
         ──────────────────────────────────────────────────────────────────

      {/* ── Initial Psych Evolution Specific ── * /}
      <ReportContainer label="Initial Psych Evolution">
        <KeyValPair
          k="Safety Plan Completed"
          v={SAFETY_PLAN_COMPLETED_MAPPER[data?.safetyPlanCompleted]}
        />
        <KeyValPair
          k="Patient`s ID Verification Via"
          v={
            PATIENT_ID_VERIFICATION_METHOD_MAPPER[
              data?.patientIdVerificationMethod
            ]
          }
        />
        <KeyValPair
          k="Safety Assessment (Suicide Risk)"
          v={
            SAFETY_ASSESSMENT_SUICIDE_RISK_MAPPER[
              data?.safetyAssessmentSuicideRisk
            ]
          }
        />
      </ReportContainer>

      {/* ── Screening Dates ── * /}
      <ReportContainer label="Screening Dates">
        <KeyValPair
          k="Mental Health Screening Date"
          v={NonNullableDate(data?.mentalHealthScreeningDate) ?? ""}
        />
        <KeyValPair
          k="Interim Date"
          v={NonNullableDate(data?.interimDate) ?? ""}
        />
        <KeyValPair
          k="Update Weight Date"
          v={NonNullableDate(data?.updateWeightDate) ?? ""}
        />
      </ReportContainer>

      {/* ── Studies / Scans / Labwork ── * /}
      <RenderTextWithContainerPattern
        label="Studies, Scans, Labwork (Date and Results for Review):"
        val={data?.studiesScansLabworkReview}
      />

      {/* ── Psychiatric Evolution Plan ── * /}
      <PsychiatricEvolutionPlanMapper data={data} />
      <RenderTextWithContainerPattern
        label="Plan Review"
        val={data?.psychiatricEvolutionPlanReview}
      />

      {/* ── Clinical Instructions ── * /}
      <ClinicalInstructionsPlanMapper data={data} />
      <RenderTextWithContainerPattern
        label="Clinical Instructions Review"
        val={data?.clinicalInstructionsReview}
      />

      {/* ── Adjunct Therapy Recommendations ── * /}
      <AdjunctTherapyRecommendationsMapper data={data} />
      <RenderTextWithContainerPattern
        label="Individual Treatment/Therapy Requirements"
        val={data?.individualTreatmentTherapyRequirements}
      />
      <RenderTextWithContainerPattern
        label="Patient Goals (Time frame, duration of treatment, termination criteria)"
        val={data?.patientGoals}
      />

      {/* ── Weight Loss Therapies ── * /}
      <WeightTherapyRadioResult value={data?.weightLossTherapiesOption} />
      <RenderTextWithContainerPattern
        label="Weight loss therapies description"
        val={data?.weightLossTherapiesDescription}
      />
      <WeightTherapiesDurationRadioResult
        value={data?.weightLossTherapiesDuration}
      />
      <RenderTextWithContainerPattern
        label="Weight loss therapies duration description"
        val={data?.weightLossTherapiesDurationDescription}
      />

      {/* ── Forms to be Provided / Patient Requests ── * /}
      <FormsToBeProvidedMapper data={data} />
      <RenderTextWithContainerPattern
        label="Note for send patient forms"
        val={data?.noteForSendPatientForms}
      />
      <RenderTextWithContainerPattern
        label="Other forms provided"
        val={data?.otherFormsProvided}
      />
      <PatientRequestsMapper data={data} />
      <RenderTextWithContainerPattern
        label="Other requests note"
        val={data?.otherRequestsNote}
      />

      ────────────────────────────────────────────────────────────────── */}

      {/* ── Follow-Up ── */}
      <ReportSection label="Follow-Up">
        <RenderTextWithContainerPattern
          label="Follow Up"
          val={data?.followUp}
        />

        {/* ── Psychoeducation Session (shown below Follow Up only when required) ── */}
        <PsychoeducationSection data={data} />
      </ReportSection>
    </>
  );
};

export default InitialPsychEvolutionTemplate;
